import { useEffect, useRef } from 'react';

import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

export default function useEnsureOrderJobs(orderId: string) {
  const { ordersDispatchStore, userStore } = useStores();
  const { getJobsByOrder } = useOrdersDispatch();
  const getJobsByOrderRef = useRef(getJobsByOrder);
  getJobsByOrderRef.current = getJobsByOrder;
  const companyId = getEffectiveUserCompanyId(userStore);
  const needsOrderJobs =
    !ordersDispatchStore.orderJobs.has(orderId) &&
    ordersDispatchStore.isLoadingOrderJobs.get(orderId) !== true &&
    ordersDispatchStore.hasOrderJobsFetchFailed.get(orderId) !== true;
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Another consumer mounted in the same commit may have started the request already.
    if (!needsOrderJobs || ordersDispatchStore.isLoadingOrderJobs.get(orderId) === true) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    void getJobsByOrderRef
      .current({
        orderId,
        companyId,
        clearExistingJobs: false,
        signal: controller.signal,
      })
      .catch(() => {});
  }, [companyId, needsOrderJobs, orderId, ordersDispatchStore]);

  // Not the effect above's cleanup: starting the request flips `needsOrderJobs`.
  useEffect(() => () => controllerRef.current?.abort(), [companyId, orderId]);
}
